/**
 * Age gate arithmetic.
 *
 * Pure and separate from the screen because this is the part that has to be
 * right: it decides whether a child is admitted to an adult platform. The
 * boundary cases — someone turning 16 today, someone one day short — are a day
 * apart and produce opposite outcomes, so they are tested directly rather than
 * through a form.
 */

export const MIN_AGE = 16;

/**
 * Parse `YYYY-MM-DD` into plain numbers.
 *
 * Deliberately not `new Date(iso)`. That parses a date-only string as UTC and
 * then reports it in local time, so `1990-01-01` becomes 31 December 1989 for
 * anyone west of Greenwich — which shifts a birthday by a day and flips the
 * gate for anyone born on the boundary. Comparing plain numbers has no timezone
 * to get wrong.
 */
export const parseIsoDate = (iso) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso ?? "");
  if (!match) return null;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);

  return isRealDate(year, month, day) ? { year, month, day } : null;
};

/**
 * Rejects 31 February and friends. `new Date(1990, 1, 31)` silently rolls
 * forward to 3 March rather than failing, so a typo would be accepted as a
 * valid date a few days from the one entered.
 */
export const isRealDate = (year, month, day) => {
  if (
    !Number.isInteger(year) ||
    !Number.isInteger(month) ||
    !Number.isInteger(day)
  ) {
    return false;
  }
  if (month < 1 || month > 12 || day < 1) return false;

  const daysInMonth = new Date(year, month, 0).getDate();
  return day <= daysInMonth;
};

/** Age in whole years on `today`. */
export const ageOn = (dob, today) => {
  const todayMonth = today.getMonth() + 1;
  const todayDay = today.getDate();

  const hadBirthday =
    todayMonth > dob.month || (todayMonth === dob.month && todayDay >= dob.day);

  return today.getFullYear() - dob.year - (hadBirthday ? 0 : 1);
};

/**
 * Whether an ISO date of birth clears the gate.
 *
 * `today` is injectable so the boundary can be tested without waiting a day.
 * A malformed or future date is not old enough — failing closed, because the
 * cost of wrongly admitting a child is far higher than the cost of asking an
 * adult to re-enter their birthday.
 */
export const meetsMinimumAge = (iso, today = new Date()) => {
  const dob = parseIsoDate(iso);
  if (!dob) return false;

  const age = ageOn(dob, today);
  if (age < 0) return false; // dated in the future

  return age >= MIN_AGE;
};

/** Zero-padded `YYYY-MM-DD` from the three inputs, or null if not a real date. */
export const toIsoDate = ({ day, month, year }) => {
  const d = Number(day);
  const m = Number(month);
  const y = Number(year);

  if (!isRealDate(y, m, d)) return null;
  // Four digits required, so a part-typed "19" does not read as the year 19.
  if (String(year).trim().length !== 4) return null;

  return `${String(y).padStart(4, "0")}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
};

/** Split an ISO date back into form fields, for returning to the step. */
export const fromIsoDate = (iso) => {
  const parsed = parseIsoDate(iso);
  if (!parsed) return { day: "", month: "", year: "" };

  return {
    day: String(parsed.day).padStart(2, "0"),
    month: String(parsed.month).padStart(2, "0"),
    year: String(parsed.year),
  };
};
